import { AUTO_PADDING, FUTURE_PADDING } from '../../utils/chartUtils'

/**
 * xWindow — the X machine, geometry half.
 *
 * OWNS (writes): nothing. This file is pure — no state, no refs, no React.
 * It owns the DEFINITION of where the window is allowed to sit and where it
 * currently sits, which is the half of the X machine that had 23 copies.
 *
 * PUBLISHES: xWalls / clampEndIdx (the legal view — soft left edge), resolveEndIdx (follow
 * sentinel -> a real index), startIdxFor (the left edge of the window).
 *
 * MUST NOT: touch halfSpan, the lattice, or the Y residual; paint anything;
 * or hold the window state — viewWindowSize / viewEndIndex live in the slot
 * (useChartSlot), not here and not in FPCC. See the note at the bottom.
 */

// ── THE LEGAL X VIEW ────────────────────────────────────────────────────
// One definition of where viewEndIndex is allowed to be, for a given candle
// count and window.
//
// These walls existed, but only INSIDE the drag handler — so the drag was the
// only mover that respected them. The wheel changes winSize, startWall is a
// function of winSize, and nothing re-checked viewEndIndex afterwards: zoom
// out far enough and the view sat left of its own left wall with no operation
// responsible for noticing.
//
// Every mover clamps through this. A view that cannot leave the data cannot
// need a fallback for having left it.
//
// SOFT EDGE (x-soft-edge): the left wall is the VIEWPORT edge, not the data
// edge. endIdx may fall to 0 — the oldest candle sitting at the plot's RIGHT
// edge, i.e. the window's left side up to W-1 blank columns past history.
// The old data wall (endIdx >= min(total,W)-1, oldest pinned to the LEFT
// edge) is what stopped the drag "at the last candle" and, because it moved
// with W, what the cursor-anchored wheel kept colliding with (anchor solved
// past it → clamp → span-expand pivot → the bounce). With the wall at 0 a
// cursor over real data can never reach it. startIdxFor floors at 0, and
// every painter/hit-test is already right-anchored on endIdx (the same
// geometry as a short history with total < W), so the blank columns need no
// special casing. The forward wall is unchanged (FUTURE_PADDING).
// winSize is kept in the signature for callers; the left wall no longer
// depends on it.
// eslint-disable-next-line no-unused-vars
export function xWalls(total, winSize) {
  return {
    startWall: 0,
    endWall: total - 1 + FUTURE_PADDING,
  }
}

export function clampEndIdx(endIdx, total, winSize, clampFuture = true) {
  if (!(total > 0) || endIdx === -1) return endIdx   // -1 is follow mode, always legal
  const { startWall, endWall } = xWalls(total, winSize)
  // Body-wheel passes clampFuture false once the cursor is past the live
  // edge. Every other mover still stops at the forward wall.
  const hi = clampFuture ? Math.min(endWall, endIdx) : endIdx
  return Math.max(startWall, hi)
}

/**
 * The follow sentinel resolved to a real index.
 *
 * -1 means "ride the live edge", which rests at AUTO_PADDING. Anything else is
 * a parked index, clamped to the FORWARD wall — because `total` shrinks (TF
 * fold, token switch, prune) and a previously-legal index can be past the wall
 * until something re-clamps it.
 *
 * clampFuture defaults true. Drag and every non-wheel mover keep that.
 * A parked body-wheel end passes false so a zoom that has gone past the
 * live edge is not pulled back onto it. Pass false only for that read.
 */
export function resolveEndIdx(endIdx, total, clampFuture = true) {
  if (endIdx === -1) return total - 1 + AUTO_PADDING
  return clampFuture ? Math.min(endIdx, total - 1 + FUTURE_PADDING) : endIdx
}

/**
 * Is the newest candle inside the window? The follow sentinel always is; a
 * parked index is once it reaches the last real candle.
 *
 * The Y camera reads this (it is one of the two things follow mode needs from
 * X) — which is exactly why it belongs here and not re-spelled at each site.
 */
export function isLiveInView(endIdx, total) {
  return endIdx === -1 || endIdx >= (total - 1)
}

/** Left edge of the window. Inclusive, floored at the first candle. */
export function startIdxFor(endIdx, winSize) {
  return Math.max(0, endIdx - winSize + 1)
}

/**
 * Sub-candle pan offset after a window-size change.
 * Column width is plotW/W — a leftover frac in PIXELS is a different number
 * of columns at the new W, so without this rescale the block slides until
 * the next drag. Pure; the slot's commitWindow is the only caller.
 */
export function rescaleFracPx(fracPx, prevW, nextW, plotW) {
  const p = Math.max(1, +prevW || 1)
  const n = Math.max(1, +nextW || 1)
  if (!(plotW > 0) || p === n) return +fracPx || 0
  let frac = (+fracPx || 0) * (p / n)
  const cw = plotW / n
  if (cw > 0) frac = ((frac % cw) + cw) % cw
  return frac
}

/**
 * Continuous candle index under a plot-local screen X.
 * Inverse of paint: x(a) = plotW - (end+1-a)*cw + frac, then
 * screen = camAx + (x-camAx)*camScale. camScale 1 / camAx null = no camera.
 * `end` is a resolved index (follow -1 already expanded).
 */
export function wheelAnchorIndex({ end, frac, plotW, win, screenX, camScale = 1, camAx = null }) {
  const W = Math.max(1, Math.round(+win || 1))
  const plot = Math.max(1, +plotW || 1)
  const cw = plot / W
  const scale = camScale > 0 ? camScale : 1
  const ax = Number.isFinite(camAx) ? camAx : plot
  const unscaled = ax + ((+screenX || 0) - ax) / scale
  return (+end || 0) + 1 - (plot + (+frac || 0) - unscaled) / cw
}

/**
 * end + frac so continuous index `anchorA` stays on plot-local `anchorX`
 * at window `win`. Same split the painters use: frac in [0, cw), end is the
 * rightmost candle. The left wall (end 0, frac 0: oldest candle at the plot's
 * right edge) still wins — reachable only with the cursor over blank space
 * left of history. The forward wall does not: once the cursor solution
 * passes the live edge, body-wheel must not pin the newest candle to the
 * right. pastRight marks that case and the end is left past the wall.
 * Never returns the follow sentinel — body-wheel zoom parks a real end.
 * hitStart: the solution was clamped at the left wall; the paint holds the
 * right edge (where the oldest candle is) instead of stretching about the
 * cursor, so the clamp and the camera never fight.
 */
export function placeWheelAnchor({ anchorA, anchorX, plotW, win, total }) {
  const W = Math.max(1, Math.round(+win || 1))
  const plot = Math.max(1, +plotW || 1)
  const a = +anchorA
  if (!(total > 0) || !Number.isFinite(a)) return null
  const mx = Math.max(0, Math.min(plot, Number.isFinite(+anchorX) ? +anchorX : plot))
  let R = a + (plot - mx) * W / plot
  const { startWall, endWall } = xWalls(total, W)
  const rMin = startWall + 1
  const rMax = endWall + 1
  let hitStart = false
  let pastRight = false
  if (R < rMin) { R = rMin; hitStart = true }
  else if (R > rMax) pastRight = true
  const n = Math.floor(R + 1e-9)
  const fracPart = R - n
  let end
  let f
  if (fracPart < 1e-9) {
    end = n - 1
    f = 0
  } else {
    end = n
    f = 1 - fracPart
  }
  if (end < startWall) { end = startWall; f = 0; hitStart = true }
  if (end > endWall) pastRight = true
  if (end === startWall && R <= rMin + 1e-6) f = 0
  const cw = plot / W
  return { end, frac: f * cw, w: W, hitStart, pastRight }
}

// ── STATE: the slot ─────────────────────────────────────────────────────
// viewWindowSize / viewEndIndex / fracPx live on useChartSlot as one `xWin`
// handle (refs + commitEnd / commitWindow). Geometry stays here; writes go
// through the slot so ref and state never diverge.
// Body-wheel cursor anchor (wheelAnchorIndex / placeWheelAnchor) is geometry
// only. commitWindow applies it; it does not rescale frac on that path.
// (placeSpanExpand — the zoom-out "span" mode that pinned the oldest candle
// to the LEFT edge once the anchor hit the old data wall — is gone with that
// wall; the plain cursor anchor covers it.)
